import {
  COLLAPSIBLE_HEADER_CONTENT_TOP_PADDING,
  CollapsibleScreenHeader,
} from "@/components/prooftree/CollapsibleScreenHeader"
import { SkillChip } from "@/components/prooftree/SkillChip"
import { Box } from "@/components/ui/box"
import { Button, ButtonText } from "@/components/ui/button"
import { HStack } from "@/components/ui/hstack"
import { Text } from "@/components/ui/text"
import { VStack } from "@/components/ui/vstack"
import { Colors } from "@/constants/Colors"
import {
  createTaskFromCoachAction,
  getCoachAction,
  getContact,
  getDomain,
  getGoal,
  getSkill,
} from "@/data/prooftree"
import { saveEqCoachAction, saveEqCoachActionAsTask } from "@/services/eqParityBackend"
import { router, useLocalSearchParams } from "expo-router"
import {
  Award,
  BookOpenText,
  CalendarPlus,
  Check,
  CheckCircle2,
  ChevronDown,
  ClipboardCheck,
  HeartHandshake,
  Lightbulb,
  Link2,
  LucideIcon,
  Save,
  Sparkles,
  UserRound,
  X,
} from "lucide-react-native"
import { useRef, useState } from "react"
import { Animated, Platform, TouchableOpacity } from "react-native"
import { SafeAreaView } from "react-native-safe-area-context"

export default function CoachActionDetailScreen() {
  const { id } = useLocalSearchParams<{ id: string }>()
  const action = getCoachAction(id)
  const task = createTaskFromCoachAction(action)
  const skill = getSkill(action.skillId)
  const domain = getDomain(skill.domainId)
  const color = domain?.color ?? Colors.dark.primary
  const contact = getContact(action.contactId)
  const goal = getGoal(action.goalId)
  const [showPlan, setShowPlan] = useState(false)
  const [goalAccepted, setGoalAccepted] = useState(Boolean(goal))
  const [saved, setSaved] = useState(false)
  const [saveMessage, setSaveMessage] = useState("Static Coach templates are ready.")
  const [savingMode, setSavingMode] = useState<"task" | "save" | null>(null)
  const scrollY = useRef(new Animated.Value(0)).current

  const detailedPlan = {
    timing: action.timing,
    effort: action.effort,
    quickTip: action.quickTip,
    mindset: action.mindset,
    phrases: action.phrases,
    preparation: action.preparation,
    expectedOutcome: action.expectedOutcome,
    taskTitle: action.taskTitle,
  }

  async function saveForLater() {
    if (savingMode) return

    setSavingMode("save")
    const result = await saveEqCoachAction({
      skillId: action.skillId,
      title: action.title,
      description: action.description,
      goalId: goalAccepted ? action.goalId : null,
      contactId: action.contactId,
      templateId: action.id,
      detailedPlan,
    })
    setSaved(result.persisted)
    setSaveMessage(result.message)
    setSavingMode(null)
  }

  async function addToTasks() {
    if (savingMode) return

    setSavingMode("task")
    const result = await saveEqCoachActionAsTask({
      skillId: action.skillId,
      title: action.title,
      description: action.description,
      goalId: goalAccepted ? action.goalId : null,
      contactId: action.contactId,
      templateId: action.id,
      detailedPlan,
      taskTitle: action.taskTitle,
      level: task.checkpoint,
      visibility: "private",
    })
    setSaveMessage(result.message)
    setSavingMode(null)
    router.push({
      pathname: "/(tabs)/tasks",
      params: {
        coachActionId: action.id,
        goalLinked: goalAccepted ? "1" : "0",
        taskId: result.persisted ? result.id : "",
        taskPersisted: result.persisted ? "1" : "0",
        taskMessage: result.message,
      },
    })
  }

  return (
    <SafeAreaView
      className="flex-1"
      edges={["top"]}
      style={{ backgroundColor: Colors.dark.surface }}
    >
      <CollapsibleScreenHeader
        scrollY={scrollY}
        eyebrow="Action Preparation"
        title={skill.title}
        subtitle="Prepare the smallest useful version of this real-world rep."
        accentColor={color}
        backFallbackHref={`/coach/${skill.id}`}
        statusText={saved ? "Saved" : saveMessage}
      />
      <Animated.ScrollView
        className="flex-1"
        contentContainerStyle={{
          padding: 20,
          paddingTop: COLLAPSIBLE_HEADER_CONTENT_TOP_PADDING,
          paddingBottom: Platform.OS === "web" ? 144 : 128,
        }}
        showsVerticalScrollIndicator={false}
        scrollEventThrottle={16}
        onScroll={Animated.event([{ nativeEvent: { contentOffset: { y: scrollY } } }], {
          useNativeDriver: false,
        })}
      >
        <Box
          className="mb-5 rounded-3xl border p-5"
          style={{
            backgroundColor: `${Colors.dark.primary}1F`,
            borderColor: `${Colors.dark.primary}61`,
          }}
        >
          <HStack className="mb-4 items-start gap-3">
            <Box
              className="h-12 w-12 items-center justify-center rounded-2xl"
              style={{ backgroundColor: `${color}22` }}
            >
              <Lightbulb color={color} size={22} />
            </Box>
            <VStack className="flex-1 gap-1">
              <Text className="text-xs font-bold" style={{ color }}>
                WHAT TO DO
              </Text>
              <Text className="text-2xl font-bold text-white">{action.title}</Text>
              <Text className="text-sm leading-5" style={{ color: Colors.dark.textPrimary }}>
                {action.description}
              </Text>
            </VStack>
          </HStack>

          <VStack className="gap-3">
            {contact ? (
              <InfoRow
                icon={UserRound}
                label="With"
                value={`${contact.name} · ${contact.role}`}
                color={color}
                onPress={() =>
                  router.push({
                    pathname: "/contact/[id]",
                    params: { id: contact.id },
                  })
                }
              />
            ) : null}
            <InfoRow icon={Sparkles} label="Quick advice" value={action.quickTip} color={color} />
          </VStack>
        </Box>

        {goal ? (
          <Box
            className="mb-5 rounded-3xl border p-5"
            style={{
              backgroundColor: goalAccepted
                ? `${Colors.dark.growth}1F`
                : Colors.dark.surfaceSecondary,
              borderColor: goalAccepted ? `${Colors.dark.growth}66` : "rgba(255,255,255,0.08)",
            }}
          >
            <HStack className="items-start gap-3">
              <Box
                className="h-10 w-10 items-center justify-center rounded-2xl"
                style={{ backgroundColor: `${Colors.dark.growth}22` }}
              >
                <Link2 color={Colors.dark.growth} size={18} />
              </Box>
              <VStack className="flex-1 gap-2">
                <Text className="text-xs font-bold" style={{ color: Colors.dark.growth }}>
                  GOAL LINK SUGGESTION
                </Text>
                <Text className="font-bold text-white">{goal.title}</Text>
                <Text className="text-sm leading-5" style={{ color: Colors.dark.textSecondary }}>
                  {goal.context}
                </Text>
                <HStack className="gap-2">
                  <GoalToggle
                    active={goalAccepted}
                    icon={Check}
                    label="Accept"
                    color={Colors.dark.growth}
                    onPress={() => setGoalAccepted(true)}
                  />
                  <GoalToggle
                    active={!goalAccepted}
                    icon={X}
                    label="Skip"
                    color={Colors.dark.textSecondary}
                    onPress={() => setGoalAccepted(false)}
                  />
                </HStack>
              </VStack>
            </HStack>
          </Box>
        ) : null}

        <TouchableOpacity activeOpacity={0.86} onPress={() => setShowPlan((current) => !current)}>
          <Box
            className="mb-5 rounded-3xl border p-5"
            style={{
              backgroundColor: Colors.dark.surfaceSecondary,
              borderColor: `${color}44`,
            }}
          >
            <HStack className="items-center justify-between gap-3">
              <HStack className="flex-1 items-center gap-3">
                <Box
                  className="h-10 w-10 items-center justify-center rounded-2xl"
                  style={{ backgroundColor: `${color}22` }}
                >
                  <BookOpenText color={color} size={18} />
                </Box>
                <VStack className="flex-1">
                  <Text className="font-bold text-white">Show Detailed Plan</Text>
                  <Text className="text-sm" style={{ color: Colors.dark.textSecondary }}>
                    Mindset, phrases, preparation, and expected result
                  </Text>
                </VStack>
              </HStack>
              <ChevronDown
                color={color}
                size={20}
                style={{ transform: [{ rotate: showPlan ? "180deg" : "0deg" }] }}
              />
            </HStack>
          </Box>
        </TouchableOpacity>

        {showPlan ? (
          <VStack className="mb-6 gap-4">
            <PlanSection
              icon={HeartHandshake}
              title="Mindset"
              color={color}
              items={[action.mindset]}
            />
            <PlanSection
              icon={ClipboardCheck}
              title="Key phrases"
              color={color}
              items={action.phrases}
            />
            <PlanSection
              icon={CalendarPlus}
              title="How to prepare"
              color={color}
              items={action.preparation}
            />
            <PlanSection
              icon={CheckCircle2}
              title="Expected result"
              color={color}
              items={[action.expectedOutcome]}
            />
          </VStack>
        ) : null}

        <Box
          className="rounded-3xl border p-4"
          style={{
            backgroundColor: Colors.dark.surfaceSecondary,
            borderColor: "rgba(255,255,255,0.08)",
          }}
        >
          <HStack className="mb-3 items-center gap-2">
            <Sparkles color={color} size={17} />
            <Text className="font-bold text-white">Task preview</Text>
          </HStack>
          <Text className="mb-3 text-sm leading-5" style={{ color: Colors.dark.textSecondary }}>
            When added, this task is saved with the skill tag and
            {goalAccepted && goal ? ` the goal "${goal.title}"` : " no accepted goal link"} when
            Supabase is available.
          </Text>
          <Text className="mb-3 text-xs font-bold" style={{ color }}>
            {saveMessage}
          </Text>
          <SkillChip skillId={task.skillIds[0]} compact />
        </Box>
      </Animated.ScrollView>

      <Box
        className="absolute bottom-0 left-0 right-0 border-t px-5 pb-6 pt-4"
        style={{
          backgroundColor: "rgba(10, 20, 20, 0.96)",
          borderColor: `${Colors.dark.primary}2E`,
        }}
      >
        <VStack className="gap-3">
          <Button
            size="xl"
            className="rounded-2xl"
            style={{ backgroundColor: Colors.dark.growth }}
            disabled={savingMode === "task"}
            onPress={addToTasks}
          >
            <CalendarPlus color={Colors.dark.primaryForeground} size={18} />
            <ButtonText className="font-bold" style={{ color: Colors.dark.primaryForeground }}>
              {savingMode === "task" ? "Adding..." : "Add to My Tasks"}
            </ButtonText>
          </Button>
          <HStack className="gap-3">
            <Button
              size="lg"
              variant="outline"
              className="flex-1 rounded-2xl border"
              style={{ borderColor: `${Colors.dark.proof}66` }}
              onPress={() =>
                router.push({
                  pathname: "/proof/new",
                  params: { skillId: skill.id, level: task.checkpoint },
                })
              }
            >
              <Award color={Colors.dark.proof} size={16} />
              <ButtonText className="font-bold" style={{ color: Colors.dark.proof }}>
                I Did This
              </ButtonText>
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="flex-1 rounded-2xl border"
              style={{ borderColor: saved ? `${color}88` : "rgba(255,255,255,0.14)" }}
              disabled={savingMode === "save"}
              onPress={saveForLater}
            >
              <Save color={saved ? color : Colors.dark.textSecondary} size={16} />
              <ButtonText
                className="font-bold"
                style={{ color: saved ? color : Colors.dark.textSecondary }}
              >
                {savingMode === "save" ? "Saving..." : saved ? "Saved" : "Save for Later"}
              </ButtonText>
            </Button>
          </HStack>
        </VStack>
      </Box>
    </SafeAreaView>
  )
}

function InfoRow({
  icon: Icon,
  label,
  value,
  color,
  onPress,
}: {
  icon: LucideIcon
  label: string
  value: string
  color: string
  onPress?: () => void
}) {
  const content = (
    <HStack
      className="items-start gap-3 rounded-2xl border p-3"
      style={{ borderColor: `${color}33` }}
    >
      <Icon color={color} size={17} />
      <VStack className="flex-1 gap-1">
        <Text className="text-xs font-bold" style={{ color }}>
          {label}
        </Text>
        <Text className="text-sm leading-5" style={{ color: Colors.dark.textSecondary }}>
          {value}
        </Text>
      </VStack>
    </HStack>
  )

  if (!onPress) return content

  return (
    <TouchableOpacity activeOpacity={0.86} onPress={onPress}>
      {content}
    </TouchableOpacity>
  )
}

function GoalToggle({
  active,
  icon: Icon,
  label,
  color,
  onPress,
}: {
  active: boolean
  icon: LucideIcon
  label: string
  color: string
  onPress: () => void
}) {
  return (
    <TouchableOpacity activeOpacity={0.86} className="flex-1" onPress={onPress}>
      <Box
        className="rounded-2xl border px-3 py-3"
        style={{
          backgroundColor: active ? `${color}20` : Colors.dark.surface,
          borderColor: active ? `${color}88` : "rgba(255,255,255,0.08)",
        }}
      >
        <HStack className="items-center justify-center gap-2">
          <Icon color={color} size={16} />
          <Text className="text-sm font-bold" style={{ color }}>
            {label}
          </Text>
        </HStack>
      </Box>
    </TouchableOpacity>
  )
}

function PlanSection({
  icon: Icon,
  title,
  color,
  items,
}: {
  icon: LucideIcon
  title: string
  color: string
  items: string[]
}) {
  return (
    <Box
      className="rounded-3xl border p-5"
      style={{
        backgroundColor: Colors.dark.surfaceSecondary,
        borderColor: `${color}44`,
      }}
    >
      <HStack className="mb-3 items-center gap-3">
        <Box
          className="h-10 w-10 items-center justify-center rounded-2xl"
          style={{ backgroundColor: `${color}22` }}
        >
          <Icon color={color} size={18} />
        </Box>
        <Text className="text-lg font-bold text-white">{title}</Text>
      </HStack>
      <VStack className="gap-2">
        {items.map((item) => (
          <HStack key={item} className="items-start gap-2">
            <CheckCircle2 color={color} size={15} />
            <Text className="flex-1 text-sm leading-5" style={{ color: Colors.dark.textSecondary }}>
              {item}
            </Text>
          </HStack>
        ))}
      </VStack>
    </Box>
  )
}
